import React, { useEffect, useState } from 'react';
import { FONTS } from '../../styles/tokens';
import {
  fetchGitHubStatus, fetchGitHubInstallations, fetchGitHubRepos, fetchGitHubBranches,
  type GitHubInstallation, type GitHubRepo, type GitHubBranch,
} from '../../lib/github-api';
import { RepoPicker } from './RepoPicker';
import { BranchPicker } from './BranchPicker';

interface Colors { text: string; textSecondary: string; card: string; border: string }

export interface GitHubSelection {
  installationId: number;
  owner: string;
  repo: string;
  branch: string;
}

interface GitHubConnectorProps {
  token: string;
  colors: Colors;
  onSelect: (selection: GitHubSelection | null) => void;
}

/**
 * Top-level "From GitHub" panel — shows the install CTA when no
 * installation exists, then walks the user through installation → repo →
 * branch selection. Fires `onSelect` with the final triple so the parent
 * can enable the deploy button.
 */
export function GitHubConnector(props: GitHubConnectorProps): React.JSX.Element {
  const [configured, setConfigured] = useState(true);
  const [installUrl, setInstallUrl] = useState('');
  const [installations, setInstallations] = useState<GitHubInstallation[]>([]);
  const [installationId, setInstallationId] = useState<number | null>(null);
  const [repos, setRepos] = useState<GitHubRepo[]>([]);
  const [repo, setRepo] = useState<GitHubRepo | null>(null);
  const [branches, setBranches] = useState<GitHubBranch[]>([]);
  const [branch, setBranch] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      const status = await fetchGitHubStatus(props.token);
      if (cancelled) return;
      if (status) { setConfigured(status.configured); setInstallUrl(status.installUrl); }
      if (status?.configured) {
        const installationList = await fetchGitHubInstallations(props.token);
        if (cancelled) return;
        setInstallations(installationList);
        if (installationList.length === 1) setInstallationId(installationList[0].installation_id);
      }
      setLoading(false);
    }
    void load();
    return () => { cancelled = true; };
  }, [props.token]);

  useEffect(() => {
    if (installationId === null) { setRepos([]); return; }
    let cancelled = false;
    fetchGitHubRepos(props.token, installationId).then(list => {
      if (!cancelled) setRepos(list);
    });
    return () => { cancelled = true; };
  }, [props.token, installationId]);

  useEffect(() => {
    if (installationId === null || !repo) { setBranches([]); return; }
    let cancelled = false;
    fetchGitHubBranches(props.token, installationId, repo.owner, repo.name).then(list => {
      if (cancelled) return;
      setBranches(list);
      setBranch(repo.defaultBranch);
    });
    return () => { cancelled = true; };
  }, [props.token, installationId, repo]);

  useEffect(() => {
    if (installationId !== null && repo && branch) {
      props.onSelect({ installationId, owner: repo.owner, repo: repo.name, branch });
    } else {
      props.onSelect(null);
    }
  }, [installationId, repo, branch]);

  if (!configured) {
    return (
      <Notice colors={props.colors}>
        <p style={{ margin: '0 0 8px' }}>
          No GitHub integration is configured. For dev, the fastest path is a Personal Access Token:
        </p>
        <ol style={{ margin: '0 0 8px 18px', padding: 0, fontSize: '12px' }}>
          <li>Create a PAT at <a href="https://github.com/settings/tokens?type=beta" target="_blank" rel="noreferrer" style={{ color: '#01A9ED' }}>github.com/settings/tokens</a> with <code>repo</code> + <code>contents:read</code>.</li>
          <li>Set <code>GITHUB_DEV_PAT=&lt;token&gt;</code> in your <code>.env</code> (or export it before running the server).</li>
          <li>Restart the hub server.</li>
        </ol>
        <p style={{ margin: 0, fontSize: '11px' }}>
          For prod, register a real GitHub App and set the <code>GITHUB_APP_*</code> env vars instead.
        </p>
      </Notice>
    );
  }
  if (loading) return <Notice colors={props.colors} text="Loading GitHub installations…" />;
  if (installations.length === 0) {
    return (
      <Notice colors={props.colors}>
        Install the Vertex GitHub App on the repo you want to deploy.
        {installUrl && (
          <>
            {' '}
            <a href={installUrl} target="_blank" rel="noreferrer" style={{ color: '#01A9ED' }}>Install →</a>
          </>
        )}
      </Notice>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Hide the Installation picker when there's only one — it's noise otherwise. */}
      {installations.length > 1 && (
        <InstallationSelect
          installations={installations}
          value={installationId}
          onChange={setInstallationId}
          colors={props.colors}
        />
      )}
      {installationId !== null && (
        <RepoPicker repos={repos} value={repo} onChange={setRepo} colors={props.colors} />
      )}
      {repo && (
        <BranchPicker branches={branches} value={branch} onChange={setBranch} colors={props.colors} />
      )}
    </div>
  );
}

/** Installation dropdown — usually a single item but handles multiple. */
function InstallationSelect(props: {
  installations: GitHubInstallation[];
  value: number | null;
  onChange: (id: number) => void;
  colors: Colors;
}): React.JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.15em', color: props.colors.textSecondary, textTransform: 'uppercase' }}>
      Installation
      <select
        value={props.value ?? ''}
        onChange={e => props.onChange(Number(e.target.value))}
        style={{ padding: '8px', borderRadius: '6px', border: `1px solid ${props.colors.border}`, background: props.colors.card, color: props.colors.text }}
      >
        <option value="">Select an installation…</option>
        {props.installations.map(installation => (
          <option key={installation.installation_id} value={installation.installation_id}>
            {installation.account_login}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Generic dashed-border notice panel. */
function Notice(props: { colors: Colors; text?: string; children?: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ padding: '14px', borderRadius: '8px', border: `1px dashed ${props.colors.border}`, color: props.colors.textSecondary, fontFamily: FONTS.body, fontSize: '12px' }}>
      {props.text ?? props.children}
    </div>
  );
}
